// This component displays file attachments with appropriate icons based on file type
// Updated to support compact mode for use in file selector dialog
import { FileText, FileSpreadsheet, FileType, Image, File, X, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export interface FileAttachmentProps {
  id: string
  name: string
  mimeType: string
  source: 'google_drive' | 'upload' | 'url'
  onRemove?: (id: string) => void
  onClick?: (id: string) => void
  selected?: boolean
  compact?: boolean // Add compact prop for simplified display
  isInitialFile?: boolean // Add isInitialFile prop to indicate initial files
}

export function FileAttachment({
  id,
  name,
  mimeType,
  source,
  onRemove,
  onClick,
  selected = false,
  compact = false,
  isInitialFile = false
}: FileAttachmentProps) {
  // Function to get file icon based on MIME type
  const getFileIcon = () => {
    if (mimeType === 'application/vnd.google-apps.document') {
      return <FileText className="h-4 w-4 text-blue-500" />
    } else if (mimeType === 'application/vnd.google-apps.spreadsheet') {
      return <FileSpreadsheet className="h-4 w-4 text-green-500" />
    } else if (mimeType === 'application/pdf') {
      return <FileType className="h-4 w-4 text-red-500" />
    } else if (mimeType.startsWith('image/')) {
      return <Image className="h-4 w-4 text-purple-500" />
    } else {
      return <File className="h-4 w-4 text-gray-500" />
    }
  }

  // Function to get file type label based on MIME type
  const getFileTypeLabel = () => {
    if (mimeType === 'application/vnd.google-apps.document') {
      return 'Google Doc'
    } else if (mimeType === 'application/vnd.google-apps.spreadsheet') {
      return 'Google Sheet'
    } else if (mimeType === 'application/pdf') {
      return 'PDF'
    } else if (mimeType.startsWith('image/')) {
      return 'Image'
    } else if (mimeType === 'text/plain') {
      return 'Text'
    } else if (mimeType === 'text/markdown') {
      return 'Markdown'
    } else {
      return 'File'
    }
  }

  // Function to get source icon
  const getSourceIcon = () => {
    if (source === 'google_drive') {
      return <ExternalLink className="h-3 w-3 text-muted-foreground" />
    }
    return null
  }

  // Function to get source label
  const getSourceLabel = () => {
    if (isInitialFile) {
      return source === 'google_drive' ? 'Initial (Google Drive)' : 'Initial (Upload)';
    }
    return source === 'google_drive' ? 'Google Drive' : 'Upload';
  }

  // If compact mode is enabled, render a simplified version
  if (compact) {
    return (
      <div className="flex items-center gap-2 w-full">
        <div className="flex-shrink-0">
          {getFileIcon()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{name}</div>
          <div className="text-xs text-muted-foreground">
            {getFileTypeLabel()} • {getSourceLabel()}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={cn(
        "group flex items-center gap-2 p-2 rounded-md border text-sm",
        "hover:bg-muted/50 transition-colors duration-200",
        "cursor-pointer",
        selected && "ring-2 ring-primary/50 bg-muted/30"
      )}
      onClick={() => onClick?.(id)}
    >
      <div className="flex-shrink-0">
        {getFileIcon()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{name}</div>
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <span>{getFileTypeLabel()}</span>
          <span>•</span>
          <span className="flex items-center gap-0.5">
            {getSourceLabel()}
            {getSourceIcon()}
          </span>
        </div>
      </div>
      {onRemove && (
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(id);
          }}
        >
          <X className="h-3 w-3" />
          <span className="sr-only">Remove</span>
        </Button>
      )}
    </div>
  )
} 